import { Alert, Collapse, Stack } from '@mui/material';
import { CustomCellEditorProps } from 'ag-grid-react';
import { useCallback, useMemo, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { RendererType } from '../../../data-models/field-definition';
import {
  InvalidEmailsErrorMessage,
  validateEmail,
  validateEmails,
} from '../../../pages/KPI/components/KPICompanies/components/KPIAssignRequestModal/emailArrayValidator';
import { cellEditorWrapperCss } from '../../../theme/gridStyle';
import { createFormField } from '../../../view-models/form.view-model';
import { FieldFactory } from '../../Form/FieldFactory';
import { colors } from '../../../theme/colors';

export interface IEmailArrayCellEditorProps<T = unknown> extends CustomCellEditorProps<T> {
  fieldPath: string;
  extraValidator?: (data: T, currentValue: string[] | undefined | null) => string | null;
}

export function EmailArrayCellEditor<T = unknown>(props: IEmailArrayCellEditorProps<T>) {
  const { value, onValueChange, stopEditing, fieldPath, extraValidator } = props;
  const [validationError, setValidationError] = useState<string | null>(null);
  // Internal state to display all user input (including invalid emails)
  // AG Grid value only receives valid emails via onValueChange
  const [internalValue, setInternalValue] = useState<string[]>(value ?? []);

  const formField = useMemo(() => {
    return createFormField({
      key: fieldPath,
      renderer: RendererType.freeLabels,
      autoFocus: true,
      rendererMeta: {
        multi: true,
        values: internalValue.map((email) => ({
          value: email,
          displayName: email,
        })),
      },
    });
  }, [fieldPath, internalValue]);

  const validateValue = useCallback((emails: string[]): boolean => {
    if (!emails || emails.length === 0) {
      return true;
    }
    return validateEmails(emails);
  }, []);

  const handleChange = useCallback(
    (newValue: string[]) => {
      setInternalValue(newValue);

      const validEmails = newValue.filter((email) => validateEmail(email));
      const hasInvalidEmails = validEmails.length !== newValue.length;

      setValidationError(hasInvalidEmails ? InvalidEmailsErrorMessage : null);
      onValueChange(validEmails);
    },
    [onValueChange]
  );

  const handleBlur = useCallback(() => {
    const isValid = validateValue(internalValue);
    if (isValid) {
      stopEditing();
    } else {
      setValidationError(InvalidEmailsErrorMessage);
    }
  }, [internalValue, validateValue, stopEditing]);

  const formProps = useMemo(
    () =>
      ({
        onChange: handleChange,
        onBlur: handleBlur,
        value: internalValue,
      }) as unknown as ControllerRenderProps,
    [handleChange, handleBlur, internalValue]
  );

  const extraValidatorWarning = validationError ? null : extraValidator?.(props.data, internalValue);

  return (
    <Stack
      css={cellEditorWrapperCss}
      sx={{
        minWidth: 200,
        width: 'fit-content',
        p: 1,
        background: colors.primary[0],
        '& .MuiAutocomplete-inputRoot': {
          flexWrap: 'wrap',
        },
        '& .MuiAutocomplete-input': {
          flexBasis: '100%',
          width: '100%',
        },
      }}
    >
      <FieldFactory formField={formField} formProps={formProps} />
      {validationError && <Alert severity='error'>{validationError}</Alert>}
      <Collapse in={Boolean(extraValidatorWarning) && !validationError}>
        <Alert severity='warning'>{extraValidatorWarning}</Alert>
      </Collapse>
    </Stack>
  );
}
